import { OverlayRef } from '@angular/cdk/overlay';
import { Location } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BaseComponent,
  ClaimDataService,
  MyClaim,
  SearchResult,
  SnackbarRef,
  SnackbarService,
  SnackbarType,
  UserActionService,
  getUrlParams,
} from 'src/app/shared';
import {
  ChatService,
  ClaimService,
  ConfigurationService,
  DirectDepositService,
  DisplayService,
  InpageAliasService,
  LogService,
  OpenFileService,
  RecentClaimService,
  Session,
  UserRoleService,
} from '../..';
import { ConfigurationSetting } from '../../../shared';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
import { Aliases } from '../../models/aliases';

@Component({
    selector: 'app-action-tiles',
    template: `
    <div>
      <app-loading-panel [state]="loading ? 'Loading' : 'Loaded'">
        <ng-template #content>
          <div class="app-header-4 tw-pb-2 tw-pt-2">{{ 'Actions' | translation:translationRequest}}</div>
          <div class="tw-w-full tw-flex tw-flex-wrap tw-gap-3 tw-pb-2">
            @for (item of items; track item) {
              <div
                class="container tw-flex tw-flex-col tw-items-center  tw-border  tw-justify-center"
                tabindex="0"
                [attr.aria-label]="item.label"
                role="link"
                [attr.alt]="item.label"
                [style.cursor]="item.loading ? 'default' : 'pointer'"
              [ngClass]="{
                'tw-drop-shadow-md': !item.loading,
                'hover:tw-drop-shadow-lg': !item.loading,
                'hover:tw-border-sky-700': !item.loading,
              }"
                (click)="handleAction(item)"
                (keydown.enter)="handleAction(item)"
                >
                @if (item.loading) {
                  <div
                    class="app-spinner-grey-24 tw-py-2 tw-mb-2"
                  ></div>
                }
                @if (!item.loading) {
                  @if (item.isSvgIcon === true) {
                    <mat-icon
                      class="icon"
                      alt=""
                      [svgIcon]="item.icon"
                    ></mat-icon>
                  }
                  @if (item.isSvgIcon === false) {
                    <img
                      class="icon"
                      alt=""
                      src="assets/images/actiontilesimg/{{ item.imgName }}"
                      />
                    }
                  }
                  <div
                    class="label"
                [ngClass]="{
                'app-color-teal': !item.loading,
                'app-color-input-grey': item.loading,
              }"
                    >
                    {{ item.label | translation:translationRequest}}
                  </div>
                  @if (item.help) {
                    <div class="help">
                      <app-help-button
                        [message]="item.help"
                        (click)="$event.stopPropagation()"
                        [id]="'actionTiles'"
                      ></app-help-button>
                    </div>
                  }
                </div>
              }
            </div>
          </ng-template>
        </app-loading-panel>
      </div>
      <ng-template #chatWarning>
        <app-dialog-container
          label="Chat in progress"
          [headerLevel]="2"
          [headerId]="'charProgress'"
          aria-labelledby="'chatProgress'"
          >
          <div class="tw-pt-2 tw-pb-4 tw-w-80">
            Press new chat to close the active chat session and start a new chat.
          </div>
          <app-action-row class="tw-pt-4">
            <app-button
              right2
              emphasis="High"
              matDialogClose
              (click)="openCareyChat()"
              label="New chat"
              ></app-button
              >
              <app-button right1 emphasis="Low" matDialogClose label="Cancel" ></app-button>
            </app-action-row>
          </app-dialog-container>
        </ng-template>
        <ng-template #auditorUnavailable>
          <app-dialog-container
            label="Feature Unavailable"
            [headerLevel]="2"
            [headerId]="'featureUnavailable'"
            aria-labelledby="'featureUnavailable'"
            >
            <div class="tw-pt-2 tw-pb-4 tw-w-80">
              This feature is unavailable for auditors.
            </div>
            <app-action-row class="tw-pt-4">
              <app-button right1 emphasis="High" matDialogClose label="Close" ></app-button>
            </app-action-row>
          </app-dialog-container>
        </ng-template>
    `,
    styles: [
        `
      .help {
        position: absolute;
        top: 0;
        right: 0;
        padding: 2px 4px 0 0;
      }
      .icon {
        font-size: 36px;
        width: 36px;
        height: 36px;
      }
      .label {
        font-size: 12px;
        font-weight: bold;
        text-align: center;
      }
      .container {
        height: 95px;
        width: 115px;
        padding: 6px;
        border-radius: 6px;
        background-color: white;
      }
      .suggestion {
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
        margin-left: 8px;
        padding: 2px 6px 2px 6px;
        border-radius: 4px;
        color: #1f7850;
        background-color: #e2f6e8;
      }

      .required {
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
        margin-left: 8px;
        padding: 2px 6px 2px 6px;
        border-radius: 4px;
        color: #c6180b;
        background-color: #ffd7cb;
      }

      .large-link {
        font-size: 16px;
      }
    `,
    ],
    standalone: false
})
export class ActionTilesComponent extends BaseComponent implements OnInit {
  @ViewChild('chatWarning')
  chatWarning: TemplateRef<any>;

  @ViewChild('auditorUnavailable')
  auditorUnavailable: TemplateRef<any>;

  @Input()
  source: string;

  @Input()
  claimId: string;

  @Input()
  showUploadDocuments = true;

  @Input()
  showDirectDeposit = false;

  @Input()
  showElectronicUpdates = true;

  @Input()
  showMilageReimbursement = false;

  @Input()
  showPharmacyCard = false;

  @Input()
  showLearningCenter = true;

  @Input()
  showContactExaminer = true;

  @Input()
  showReturnToWork = false;

  @Input()
  showReimbursement = false;

  @Input()
  showIntermittent = false;

  @Input()
  showRequestExtension = true;

  @Input()
  claim: SearchResult;

  @Input()
  translationRequest:TranslationRequest;

  displayErrorToastMsg: string = '';
  items: TileItem[] = [];
  myClaim: MyClaim;
  myClaims: MyClaim[];
  examinerLabel: Aliases;
  isManageDDVisiable: boolean = true;
  isPharmCardVisible: boolean = false;
  loading: boolean = true;

  overlayRef: OverlayRef;
  canUploadOnClaim: boolean = false;
  claimConfig: ConfigurationSetting;

  chatTile: TileItem;

  isManageDDAccess = async () => {
    if (this.claim != null && this.claim != undefined) {
      if (this.claimConfigService.getFeatureAccess('DirectDeposit')) {
        const isAccess = await this.ddService.hasAccessDirectDeposit({
          ClaimUID: this.claim.claimId,
          LineOfBusiness: this.claim.lob,
          Source: this.claim.source,
        });

        return isAccess;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  constructor(
    private router: Router,
    public claimConfigService: ConfigurationService,
    public inpageAliasService: InpageAliasService,
    public session: Session,
    private route: ActivatedRoute,
    public claimService: ClaimService,
    public ddService: DirectDepositService,
    public openFileService: OpenFileService,
    public recentClaimService: RecentClaimService,
    private claimDataService: ClaimDataService,
    private matIconRegistory: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private display: DisplayService,
    private userRole: UserRoleService,
    public dialog: MatDialog,
    private careyChat: ChatService,
    private snackbarService: SnackbarService,
    private userActions: UserActionService,
    private location: Location,
    private logService: LogService
  ) {
    super();
  }

  private async loadClaim() {}

  async ngOnInit() {
    const params = getUrlParams();
    this.claimId = params.claimId;
    this.source = params.source;
    this.claim = await this.claimService.getSummary(
      this.source,
      this.claimId,
      false,
      false,
      params.lob
    );

    this.examinerLabel = await this.inpageAliasService.replaceAliases(
      'examinerSingular',
      this.claimConfigService.GetConfig.configFeatures.aliases
    );
    await this.setupVisibility();
    await this.loadTileItems();
    this.loading = false;

    this.subs.sink = this.claimDataService.currentMyClaim$.subscribe(
      (claim) => {
        if (claim) {
          this.myClaim = claim;
        }
      }
    );
  }

  handleAction(item: TileItem) {
    if (item.loading) {
      return;
    }

    const action = item.action;

    const queryParams: any = {};

    if (action === 'rtw') {
      queryParams.rtw = 'submit';
      queryParams.claimId = this.claimId;
      queryParams.source = this.source;
      this.router.navigate(['/return-to-work'], {
        queryParams,
      });
    } else if (action === 'docs') {
      queryParams.focus = 'documents';
      queryParams.claimId = this.claimId;
      queryParams.source = this.source;
      queryParams.showDialog = true;
      const targetUrl = this.router
        .createUrlTree(['/upload-documents-in-documentstab'], { queryParams })
        .toString();
      if (this.location.path() === targetUrl) {
        window.location.reload(); //when coming back from upload to summary tab if the url is the same it doesnt redirect so need to reload
      } else {
        this.router.navigate(['/upload-documents-in-documentstab'], {
          queryParams,
        });
      }
    }
    // Report an Absence
    else if (action === 'raa') {
      /*       queryParams.claimId = this.claimId;
      queryParams.source = this.source;
      this.router.navigate(['/reportanabsence'], {
        queryParams,
      }); */
      this.claimService.loadIntermittentAbsence(
        this.claimId,
        this.source,
        this.router
      );
    } else if (action === 'eeu') {
      this.router.navigate([], {
        queryParams,
      });
    } else if (action === 'mdd') {
      queryParams.claimId = this.claimId;
      queryParams.source = this.source;
      this.router.navigate(['/managedirectdeposit'], {
        queryParams,
      });
    } else if (action === 'mr') {
      queryParams.claimId = this.claimId;
      queryParams.source = this.source;
      this.router.navigate(['/mileage-reimbursement'], {
        queryParams,
      });
    } else if (action === 'vpc') {
      queryParams.claimId = this.claimId;
      queryParams.source = this.source;
      this.router.navigate(['/pharmacy-card'], {
        queryParams,
      });
    } else if (action === 'vhr') {
      queryParams.claimId = this.claimId;
      queryParams.source = this.source;
      queryParams.claim =
        this.claim.claimDescription + ' - ' + this.claim.claimNumber;
      queryParams.lob = this.claim.lob;
      queryParams.origin = 'claimSummary';
      this.router.navigate(['/help'], {
        queryParams,
      });
    } else if (action === 'ccp') {
      /*     else if (action === 'ce') {
      this.gotoContactEmaminer();
      this.logFeature('CommunicationCenter');
    }  */
      queryParams.ExpandCCP = 'true';
      queryParams.focus = 'changepreferences';
      queryParams.claimId = this.claimId;
      queryParams.source = this.source;
      this.router.navigate(['../PreferencesPage'], {
        queryParams,
      });
    } else if (action === 'fmp') {
      this.openFileLink();
    } else if (action === 'chat') {
      /*     else if (action === 'awl') {
      this.addToWatchList();
      this.logFeature('AddToWatchList');
    }  */
      this.tryOpenCareyChat();
    } else if (action === 'mws') {
      queryParams.claimId = this.claimId;
      queryParams.source = this.source;
      this.router.navigate(['../my-work-schedule'], {
        queryParams,
      });
    } else if (action === 'inv') {
      queryParams.claimId = this.claimId;
      queryParams.source = this.source;
      this.router.navigate(['/inventory-submission'], {
        queryParams,
      });
    }
    /*else if (action === 'pend-acc') {
      queryParams.claimId = this.claimId;
      queryParams.source = this.source;
      this.router.navigate(['../accommodations-pending'], {
        queryParams,
      });
      this.logFeature('PendingAccommodations');
    }*/
    this.logFeature(item.featureName);
  }

  openFileLink() {
    this.openFileService.openfile();
  }

  logFeature(featureName: string) {
    this.logService.LogFeature(
      this.claim,
      featureName,
      'View Summary - Action Tiles'
    );
  }

  async addToWatchList() {
    const str = await this.recentClaimService.addToWatchList(this.claim);
    if (str != 'success') {
      this.displayErrorToastMsg =
        'The Watch List is full. Additional claims can be added when one or more claims have been removed.';
    }
  }

  gotoContactEmaminer() {
    const comCenter = document.getElementById('commCenterPnl');
    if (comCenter != null) {
      comCenter.scrollIntoView();
      if (
        document.getElementById('btnSendMessage') != null ||
        document.getElementById('btnSendMessage') != undefined
      ) {
        document.getElementById('btnSendMessage').click();
      }
    }
  }

  async loadTileItems() {
    this.matIconRegistory.addSvgIcon(
      'rtw',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/images/svgicons/v1_return_to_office.svg'
      )
    );
    this.matIconRegistory.addSvgIcon(
      'uploaddoc',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/images/svgicons/v1_Upload documents.svg'
      )
    );
    // this.matIconRegistory.addSvgIcon(
    //   'directdeposit',
    //   this.domSanitizer.bypassSecurityTrustResourceUrl(
    //     '../../../../assets/images/svgicons/Direct_deposit.svg'
    //   )
    // );
    this.matIconRegistory.addSvgIcon(
      'milage',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/images/svgicons/v1_Request mileage reimbursement.svg'
      )
    );
    this.matIconRegistory.addSvgIcon(
      'helpful',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/images/svgicons/Helpful_resources.svg'
      )
    );
    this.matIconRegistory.addSvgIcon(
      'commpref',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/images/svgicons/v1_Change communication preferences.svg'
      )
    );
    this.matIconRegistory.addSvgIcon(
      'mail_outline',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/images/svgicons/Comcenter.svg'
      )
    );
    this.matIconRegistory.addSvgIcon(
      'local_pharmacy',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/images/svgicons/v1_View pharmacy card.svg'
      )
    );
    this.matIconRegistory.addSvgIcon(
      'medical_services',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/images/svgicons/Medical_Provider.svg'
      )
    );
    this.matIconRegistory.addSvgIcon(
      'intermittent_absence',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/images/svgicons/Intermittent_absence.svg'
      )
    );
    this.matIconRegistory.addSvgIcon(
      'my_work_schedule',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/images/svgicons/Intermittent_absence.svg'
      )
    );
    this.matIconRegistory.addSvgIcon(
      'live_chat',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/images/svgicons/Live_chat.svg'
      )
    );
  }

  async setupVisibility() {
    this.isManageDDVisiable =
      (await this.isManageDDAccess()) && this.claim.userOwns;
    if (
      this.claimConfigService.getConfigFeatureAccess('ReturnToWork') &&
      !this.claim.isIntermittent &&
      !this.claim.isLiabilityClaim &&
      this.claim.status &&
      !this.claim.status?.toLowerCase().includes('closed') &&
      this.claim.lob !== 'AD' &&
      this.claim.source !== 'L'
    ) {
      this.items.push(
        new TileItem(
          'rtw',
          'Report return to work',
          'rtw',
          '',
          false,
          'v1_return_to_office.png',
          null,
          'ReturnToWork'
        )
      );
    }
    if (this.claim) {
      this.claimConfig = await this.claimConfigService.getClaimConfiguration(
        this.claim
      );
      this.canUploadOnClaim =
        this.claimConfig.features.indexOf('uploaddocuments') > -1;
    }
    if (this.canUploadOnClaim) {
      this.items.push(
        new TileItem(
          'docs',
          'Upload documents',
          'uploaddoc',
          '',
          false,
          'v1_Upload documents.png',
          null,
          'UploadDocuments'
        )
      );
    }
    if (this.display.reportIntermittentAbsenceByLob) {
      this.items.push(
        new TileItem(
          'raa',
          'Report an absence',
          'intermittent_absence',
          '',
          false,
          'Intermittent_absence.png',
          null,
          'SubmitIntermittentAbsence'
        )
      );
    }
    if (
      this.isManageDDVisiable &&
      this.claimConfigService.getFeatureAccess('DirectDeposit')
    ) {
      this.items.push(
        new TileItem(
          'mdd',
          'Manage direct deposit',
          '../../../../assets/images/Direct_deposit.png',
          '',
          false,
          'Direct_deposit.png',
          null,
          'DirectDeposit'
        )
      );
    }
    const listofRemovedFeatures = this.claim.featuresNotAllowedForCurrentClaim;
    if (
      this.claimConfigService.getFeatureAccess('medicalcard') &&
      (!listofRemovedFeatures ||
        (listofRemovedFeatures &&
          listofRemovedFeatures.indexOf('medicalcard') < 0))
    ) {
      this.isPharmCardVisible = true;
    }

    if (this.isPharmCardVisible && this.claim.userOwns) {
      this.items.push(
        new TileItem(
          'vpc',
          'View pharmacy card',
          'local_pharmacy',
          '',
          false,
          'v1_View pharmacy card.png',
          null,
          'MedicalCard'
        )
      );
    }

    if (
      this.claimConfigService.getFeatureAccess('mileagereimbursement') &&
      this.claim.userOwns
    ) {
      this.items.push(
        new TileItem(
          'mr',
          'Mileage reimbursement',
          'milage',
          '',
          false,
          'v1_Request mileage reimbursement.png',
          null,
          'MileageReimbursement'
        )
      );
    }

    if (
      this.claim.lob === 'PR' &&
      this.claim.status?.toLowerCase() === 'open' &&
      this.claimConfigService.getFeatureAccess('inventorysubmission') &&
      this.claim.userOwns
    ) {
      this.items.push(
        new TileItem(
          'inv',
          'Inventory submission',
          'inventory_submission',
          '',
          false,
          'Inventory_submission.png',
          null,
          'InventorySubmission'
        )
      );
    }

    this.items.push(
      new TileItem(
        'vhr',
        'Visit helpful resources',
        'helpful',
        '',
        false,
        'Helpful_resources.png',
        null,
        'Help'
      )
    );

    if (
      this.claimConfigService.getConfigFeatureAccess(
        'notificationpreferences'
      ) &&
      this.claim.userOwns
    ) {
      this.items.push(
        new TileItem(
          'ccp',
          'Communication preferences',
          'commpref',
          '',
          false,
          'v1_Change communication preferences.png',
          null,
          'NotificationPreferences'
        )
      );
    }
    if (
      this.claimConfigService.getFeatureAccess('FindaDoctor') &&
      this.claim.userOwns
    ) {
      this.items.push(
        new TileItem(
          'fmp',
          'Find a medical provider',
          'medical_services',
          '',
          false,
          'Medical_Provider.png',
          null,
          'FindaDoctor'
        )
      );
    }
    if (this.display.hasClaimChatAccess) {
      this.chatTile = new TileItem(
        'chat',
        'Chat with us',
        'live_chat',
        '',
        false,
        'Live_chat.png',
        null,
        'CareyChat'
      );
      this.items.push(this.chatTile);
    }

    if (
      this.display.myWorkSchedule &&
      (this.claim.lob == 'DS' || this.claim.lob == 'LV')
    ) {
      this.items.push(
        new TileItem(
          'mws',
          'Work Schedule',
          'my_work_schedule',
          '',
          false,
          'Intermittent_absence.png',
          null,
          'WorkSchedule'
        )
      );
    }
  }

  liveChatVisible() {
    const element = document.getElementById('lhnHelpOutCenter');
    if (element) {
      return element.classList.contains('lhnActive');
    }
    return false;
  }

  careyChatVisible() {
    const element = document.getElementById('chatcontainer');
    if (element) {
      const style = window.getComputedStyle(element);
      return style.display !== 'none';
    }
    return false;
  }

  async tryOpenCareyChat() {
    if (
      this.session.user.emulatorContext?.isEmulating &&
      this.session.user.emulatorContext?.role === 'AUDITOR'
    ) {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        arialabelledby: 'featureUnavailable',
      };
      this.dialog.open(this.auditorUnavailable);
    } else if (
      this.careyChat.isActive() ||
      this.liveChatVisible() ||
      this.careyChatVisible()
    ) {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        arialabelledby: 'chatProgress',
      };
      this.dialog.open(this.chatWarning);
    } else {
      this.openCareyChat();
    }
  }

  setChatTileLoading(loading: boolean) {
    this.chatTile.loading = loading;
  }

  async openCareyChat() {
    this.setChatTileLoading(true);

    if (this.careyChat.isActive()) {
      await this.careyChat.close();
    }

    const liveChatClose = document.getElementById('lhnWindowCloser');
    if (liveChatClose) {
      liveChatClose.focus();
      liveChatClose.click();
    }

    let snackbarRef: SnackbarRef = null;
    try {
      snackbarRef = this.snackbarService.show({
        message: 'This operation is taking a while, please wait.',
        type: SnackbarType.Basic,
        delayMs: 20000,
        dismiss: true,
      });
      await this.careyChat.initialize(
        this.claim.source,
        this.claim.claimId,
        this.claim.lob,
        this.claim.userOwns ?? false
      );
    } catch (e: any) {
      this.snackbarService.show(
        e?.message ? e.message : 'Chat error',
        SnackbarType.Error
      );
    } finally {
      this.setChatTileLoading(false);
      this.snackbarService.cancel(snackbarRef);
    }
  }
}

class TileItem {
  constructor(
    public action: string,
    public label: string,
    public icon: string,
    public help: string = '',
    public isSvgIcon: boolean = false,
    public imgName: string = '',
    public loading: boolean = false,
    public featureName: string = ''
  ) {}
}
